import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CheckCircle,
  Error as ErrorIcon,
  Link as LinkIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { authApi } from '../services/authApi';

interface Site {
  id: number;
  name: string;
  url: string;
}

interface ConnectionStatus {
  connected: boolean;
  siteId: number;
  expiresAt?: string;
}

interface ApiResponse<T> {
  data: T;
  success: boolean;
  requestId: string;
  timestamp: number;
}

/**
 * GSC Credentials Management Dashboard
 * 
 * Allows users to:
 * - View all sites and their GSC connection status
 * - Connect/disconnect sites from GSC
 * - Refresh access tokens
 * - View connection details
 */
export const GscCredentialsManagement: React.FC = () => {
  const [sites, setSites] = useState<Site[]>([]);
  const [connectionStatuses, setConnectionStatuses] = useState<Map<number, ConnectionStatus>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadSitesAndStatuses();
  }, []);

  /**
   * Load all sites and their connection statuses
   */
  const loadSitesAndStatuses = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load sites
      const sitesResponse = await api.get<ApiResponse<Site[]>>('/customer/sites');
      
      console.log('Sites API Response:', sitesResponse.data);
      console.log('Response type:', typeof sitesResponse.data);
      console.log('Is Array?', Array.isArray(sitesResponse.data));
      
      // Handle different response structures
      let sitesData: Site[] = [];
      if (Array.isArray(sitesResponse.data)) {
        // Direct array response
        sitesData = sitesResponse.data;
        console.log('Using direct array');
      } else if (sitesResponse.data?.data && Array.isArray(sitesResponse.data.data)) {
        // Wrapped in ApiResponse
        sitesData = sitesResponse.data.data;
        console.log('Using wrapped array, length:', sitesData.length);
      } else if (typeof sitesResponse.data === 'object' && sitesResponse.data !== null) {
        // Response is an object, might be a single site or error
        console.warn('Unexpected response format:', sitesResponse.data);
        sitesData = [];
      }
      
      console.log('Final sitesData:', sitesData);
      setSites(sitesData);

      // Load connection status for each site
      const statusMap = new Map<number, ConnectionStatus>();
      
      await Promise.all(
        sitesData.map(async (site) => {
          try {
            const statusResponse = await authApi.get<ConnectionStatus>(
              `/oauth/gsc/status?siteId=${site.id}`
            );
            statusMap.set(site.id, statusResponse.data);
          } catch (err) {
            // Set default "not connected" status if API fails
            console.error(`Failed to load status for site ${site.id}:`, err);
            statusMap.set(site.id, { connected: false, siteId: site.id });
          }
        })
      );

      setConnectionStatuses(statusMap);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to load sites';
      setError(String(errorMessage));
      setSites([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Navigate to OAuth wizard for a site
   */
  const handleConnect = (site: Site) => {
    console.log('Navigating to OAuth wizard for site:', site.id, site);
    navigate(`/gsc-oauth/${site.id}`);
  };

  /**
   * Open disconnect confirmation dialog
   */
  const handleDisconnectClick = (site: Site) => {
    setSelectedSite(site);
    setDisconnectDialogOpen(true);
  };

  /**
   * Disconnect a site from GSC
   */
  const handleDisconnect = async () => {
    if (!selectedSite) return;

    try {
      await authApi.delete(`/oauth/gsc/disconnect?siteId=${selectedSite.id}`);
      
      // Update local state
      const newStatuses = new Map(connectionStatuses);
      newStatuses.set(selectedSite.id, { connected: false, siteId: selectedSite.id });
      setConnectionStatuses(newStatuses);
      
      setDisconnectDialogOpen(false);
      setSelectedSite(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to disconnect');
    }
  };

  /**
   * Test connection by fetching properties
   */
  const handleTestConnection = async (site: Site) => {
    setError(null);
    
    try {
      const response = await authApi.get<any[]>(`/oauth/gsc/properties?siteId=${site.id}`);
      alert(`Connection successful! Found ${response.data.length} properties.`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Connection test failed');
    }
  };

  const getConnectionStatusChip = (siteId: number) => {
    const status = connectionStatuses.get(siteId);
    
    if (!status) {
      return <Chip label="Loading..." size="small" />;
    }

    if (status.connected) {
      return (
        <Chip
          icon={<CheckCircle />}
          label="Connected"
          color="success"
          size="small"
        />
      );
    }

    return (
      <Chip
        icon={<ErrorIcon />}
        label="Not Connected"
        color="default"
        size="small"
      />
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Google Search Console Connections
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/webtools/config')}
          >
            Back to Site Configuration
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadSitesAndStatuses}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Sites & Connections
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Manage Google Search Console connections for your sites. Connected sites will
            automatically collect search performance data daily.
          </Typography>

          {!sites || sites.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              No sites configured. Add a site first to connect to Google Search Console.
            </Alert>
          ) : (
            <List>
              {(Array.isArray(sites) ? sites : []).map((site) => {
                const status = connectionStatuses.get(site.id);
                const isConnected = status?.connected || false;

                return (
                  <ListItem
                    key={site.id}
                    divider
                    sx={{
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1">{site.name}</Typography>
                          {getConnectionStatusChip(site.id)}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {site.url}
                          </Typography>
                          {isConnected && status?.expiresAt && (
                            <Typography variant="caption" color="text.secondary">
                              Token expires: {new Date(status.expiresAt).toLocaleString()}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {isConnected ? (
                          <>
                            <Button
                              size="small"
                              startIcon={<RefreshIcon />}
                              onClick={() => handleTestConnection(site)}
                            >
                              Test
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={() => handleDisconnectClick(site)}
                            >
                              Disconnect
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<LinkIcon />}
                            onClick={() => handleConnect(site)}
                          >
                            Connect
                          </Button>
                        )}
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Information Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              What is Google Search Console?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Google Search Console is a free service by Google that helps you monitor and maintain
              your site's presence in Google Search results. By connecting it to SEO Engine, you can
              analyze search performance and run experiments to improve your rankings.
            </Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Security & Privacy
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We use OAuth 2.0 for secure authentication. Your credentials are encrypted with
              AES-256-GCM and stored securely. We only request read-only access and never modify
              your Google account or Search Console data.
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Disconnect Confirmation Dialog */}
      <Dialog open={disconnectDialogOpen} onClose={() => setDisconnectDialogOpen(false)}>
        <DialogTitle>Disconnect from Google Search Console?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to disconnect <strong>{selectedSite?.name}</strong> from
            Google Search Console? This will:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="• Stop automatic data collection" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Remove stored credentials" />
            </ListItem>
            <ListItem>
              <ListItemText primary="• Revoke access from your Google account" />
            </ListItem>
          </List>
          <Typography variant="body2" color="text.secondary">
            Historical data will be preserved, but you'll need to reconnect to collect new data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleDisconnect} color="error" variant="contained">
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GscCredentialsManagement;
